import React from 'react';
import { motion } from 'framer-motion';
import { 
  Search, 
  Globe, 
  LineChart, 
  Link as LinkIcon,
  Settings,
  Smartphone,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';
import { Link } from 'react-router-dom';

const features = [
  {
    icon: Search,
    title: 'Leitarvélabestun',
    description: 'Við hámörkum sýnileika þinn í leitarvélum með bestu aðferðum.'
  },
  {
    icon: Globe,
    title: 'Staðbundin bestun',
    description: 'Sérsniðin bestun fyrir staðbundna leit og markaði.'
  },
  {
    icon: LineChart,
    title: 'Árangursmælingar',
    description: 'Ítarlegar skýrslur og greining á árangri SEO aðgerða.'
  },
  {
    icon: LinkIcon,
    title: 'Tenglauppbygging',
    description: 'Markviss uppbygging á gæðatengingum við vefsíðuna.'
  },
  {
    icon: Settings,
    title: 'Tæknileg bestun',
    description: 'Tæknileg fínstilling á vefsíðu fyrir betri árangur.'
  },
  {
    icon: Smartphone,
    title: 'Farsímabestun',
    description: 'Sérstök áhersla á bestun fyrir farsíma og spjaldtölvur.'
  }
];

const packages = [
  {
    name: 'SEO Basic',
    price: '49.950 kr./mán',
    description: 'Grunnpakki fyrir minni fyrirtæki',
    features: [
      'Lykilorðagreining',
      'Tæknileg SEO bestun',
      'Mánaðarleg skýrsla',
      'Google My Business uppsetning',
      'Meta lýsingar og titlar',
      'Grunnstilling á Analytics',
      '3 klst. í efnisvinnslu á mánuði',
      'Símtöl á 3ja mánaða fresti'
    ]
  },
  {
    name: 'SEO Pro',
    price: '89.950 kr./mán',
    description: 'Alhliða pakki fyrir meðalstór fyrirtæki',
    features: [
      'Allt í Basic pakka',
      'Ítarleg samkeppnisgreining',
      'Tenglauppbygging',
      'Vikulegar skýrslur',
      'Staðbundin leitarbestun',
      'Schema markup uppsetning',
      '8 klst. í efnisvinnslu á mánuði',
      'Mánaðarlegir símafundir',
      'Forgangsþjónusta',
      'Samfélagsmiðlabestun'
    ],
    popular: true
  },
  {
    name: 'SEO Enterprise',
    price: '149.950 kr./mán',
    description: 'Sérsniðin þjónusta fyrir stærri fyrirtæki',
    features: [
      'Allt í Pro pakka',
      'Sérsniðin SEO stefna',
      'Alþjóðleg leitarbestun',
      'Dagleg vöktun',
      'Samþætting við markaðsaðgerðir',
      'API tengingar',
      '20 klst. í efnisvinnslu á mánuði',
      'Vikulegir símafundir',
      'VIP þjónusta',
      'Conversion Rate Optimization',
      'A/B prófanir',
      'Sérsniðnar skýrslur'
    ]
  }
];

const SEO = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <motion.div 
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl font-bold text-gray-900 mb-6">
              Leitarvélabestun sem skilar árangri
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Við hjálpum þér að auka sýnileika þinn á netinu og ná betri árangri 
              í leitarvélum með faglegri SEO þjónustu.
            </p>
            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link to="/fa-tilbod">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-[#EA295A] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#d01d4c] transition-all shadow-lg shadow-[#EA295A]/20 hover:shadow-xl hover:shadow-[#EA295A]/30 flex items-center"
                >
                  Fá SEO greiningu
                  <ArrowRight className="ml-2" />
                </motion.button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Features Section */}
      <motion.div 
        className="py-24 bg-white"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Alhliða SEO þjónusta
            </h2>
            <p className="text-xl text-gray-600">
              Við bjóðum upp á heildstæða SEO þjónustu sem skilar raunverulegum árangri
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                className="bg-gray-50 rounded-xl p-8"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#EA295A]/10 mb-6">
                  <feature.icon className="w-6 h-6 text-[#EA295A]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Packages Section */}
      <motion.div 
        className="py-24 bg-gray-50"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              SEO þjónustupakkar
            </h2>
            <p className="text-xl text-gray-600">
              Veldu þann pakka sem hentar þínum þörfum best
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <motion.div 
                key={index}
                className={`bg-white rounded-xl p-8 shadow-sm border ${
                  pkg.popular ? 'border-[#EA295A]' : 'border-gray-100'
                }`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                {pkg.popular && (
                  <div className="bg-[#EA295A]/10 text-[#EA295A] text-sm font-medium px-3 py-1 rounded-full inline-block mb-4">
                    Vinsælast
                  </div>
                )}
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                <div className="text-3xl font-bold text-[#EA295A] mb-4">{pkg.price}</div>
                <p className="text-gray-600 mb-6">{pkg.description}</p>
                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-center text-gray-600">
                      <CheckCircle2 className="w-5 h-5 text-[#EA295A] mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link to="/fa-tilbod">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`w-full px-6 py-3 rounded-lg font-medium flex items-center justify-center ${
                      pkg.popular
                        ? 'bg-[#EA295A] text-white hover:bg-[#d01d4c]'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    } transition-all`}
                  >
                    Velja pakka
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </motion.button>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* CTA Section */}
      <motion.div 
        className="py-24 bg-white"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#EA295A] to-[#FF6B6B] rounded-2xl p-12 text-center">
            <motion.h2 
              className="text-3xl font-bold text-white mb-6"
            >
              Tilbúin að bæta sýnileika þinn?
            </motion.h2>
            <motion.p 
              className="text-xl text-white/90 mb-8 max-w-2xl mx-auto"
            >
              Láttu okkur hjálpa þér að ná betri árangri í leitarvélum með 
              faglegri SEO þjónustu
            </motion.p>
            <motion.div>
              <Link to="/fa-tilbod">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-white text-[#EA295A] px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-all shadow-lg"
                >
                  Fá SEO greiningu
                </motion.button>
              </Link>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default SEO;